import { Injectable, computed, inject } from '@angular/core';
import { attributes } from '@apps/mtjr/vendors/app/app.config';
import { AsyncState, asResult, initialState } from '@libs/core/lib/state/async-states';
import { ProductUpdateDtoMap } from '@libs/mtjr/shared/generated/dtoMaps/product-update.dto.map';
import { Dtos, Entity } from '@libs/mtjr/shared/generated/types';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { TranslateService } from '@ngx-translate/core';
import { ObjectId } from 'bson';
import { flatMap, groupBy, isEmpty, isEqual, map, uniqWith } from 'lodash-es';
import { from } from 'rxjs';

export type AttributesPickerStoreType = InstanceType<typeof AttributesPickerStore>;
export const AttributesPickerStore = signalStore(
  withState<{
    selectedAttributes: Dtos.Attribute[];
    variants: Entity.ProductVariation[];
    attributesState: AsyncState<Dtos.Attribute[]>;
    attrFilterText: string;
    lastSelectedAttribute: Dtos.Attribute | undefined;
    lastSelectedVariant?: Entity.ProductVariation | undefined;
  }>({
    selectedAttributes: [],
    variants: [],
    attributesState: initialState(),
    attrFilterText: '',
    lastSelectedAttribute: undefined,
    lastSelectedVariant: undefined,
  }),

  withComputed((store, translate = inject(TranslateService)) => ({
    selectedKeys: computed(() => store.selectedAttributes().flatMap((x) => x.values.map((y) => y.key))),
    innerValuesSearchPreview: computed(() => {
      const cState = store.attributesState();
      if (cState.success) {
        if (!store.attrFilterText()) return undefined;
        const values = cState.data.flatMap((attr) => {
          return {
            key: attr.key,
            values: attr.values.filter((val) => val.label.toLowerCase().includes(store.attrFilterText().toLowerCase())),
          };
        });
        if (values.length === 0) return undefined;
        return values.reduce(
          (acc, curr) => {
            acc[curr.key] = curr.values;
            return acc;
          },
          {} as Record<string, Dtos.AttributeValue[]>,
        );
      }
      return undefined;
    }),

    filteredAttributes: computed(() => {
      const cState = store.attributesState();
      if (cState.success) {
        if (!store.attrFilterText) return cState.data;
        return cState.data.filter((attr) => {
          const translatedLabel = translate.instant('ATTRIBUTE.' + attr.label.toUpperCase());
          return (
            translatedLabel.toLowerCase().includes(store.attrFilterText().toLowerCase()) ||
            attr.values.some((val) => val.label.toLowerCase().includes(store.attrFilterText().toLowerCase()))
          );
        });
      }
      return [];
    }),
    hasVariants: computed(() => store.variants().length > 0),
  })),

  withMethods((store, service = inject(AttributesService)) => ({
    async fetchAttributesList() {
      const result = await asResult(service.fetchAttributes());
      patchState(store, { attributesState: result });
    },

    setLastSelectedAttribute(attr: Dtos.Attribute) {
      patchState(store, { lastSelectedAttribute: attr });
    },

    setLastSelectedVariant(variant: Entity.ProductVariation) {
      patchState(store, { lastSelectedVariant: variant });
    },

    updateFilterText(text: string) {
      patchState(store, { attrFilterText: text });
    },
    setVariants(product: ProductUpdateDtoMap) {
      const variants = product.variations;
      if (variants) {
        const attrs: Entity.VariationAttribute[] = uniqWith(
          flatMap(variants, (x) => x.attributes),
          (a, b) => a.key === b.key && a.typeKey === b.typeKey,
        );
        const varAttr: Dtos.Attribute[] = [];
        const grouped = groupBy(attrs, 'typeKey');

        for (const key in grouped) {
          const values = grouped[key];
          if (isEmpty(values)) continue;
          varAttr.push({
            key: key,
            label: values[0].typeKey,
            type: values[0].type,
            values: values.map(attributeValueFromVariationAttribute),
            fakeForTesting: false,
          });
        }
        patchState(store, { variants, selectedAttributes: varAttr });
      }
    },
    toggleAttribute(selectedAttr: Dtos.Attribute, selectedVal: Dtos.AttributeValue, subKey?: string) {
      const selectedAttributes = [...store.selectedAttributes()];
      const value = subKey ? { ...selectedVal, key: subKey } : selectedVal;
      const existingAttrIndex = selectedAttributes.findIndex((x) => x.key === selectedAttr.key);

      let updatedAttributes: Dtos.Attribute[];

      if (existingAttrIndex === -1) {
        // Add new attribute with value
        updatedAttributes = [...selectedAttributes, { ...selectedAttr, values: [value] }];
      } else {
        const existingAttr = selectedAttributes[existingAttrIndex];
        const existingValueIndex = existingAttr.values.findIndex((x) => x.key === value.key);

        if (existingValueIndex === -1) {
          // Add value to existing attribute
          selectedAttributes[existingAttrIndex] = {
            ...existingAttr,
            values: [...existingAttr.values, value],
          };
          updatedAttributes = selectedAttributes;
        } else {
          // Remove value from existing attribute
          const updatedValues = existingAttr.values.filter((x) => x.key !== value.key);

          if (updatedValues.length === 0) {
            // Remove entire attribute if no values left
            updatedAttributes = selectedAttributes.filter((_, index) => index !== existingAttrIndex);
          } else {
            // Update attribute with remaining values
            selectedAttributes[existingAttrIndex] = {
              ...existingAttr,
              values: updatedValues,
            };
            updatedAttributes = selectedAttributes;
          }
        }
      }

      const variants = generateVariants(updatedAttributes);
      patchState(store, { selectedAttributes: updatedAttributes, variants });
    },

    updateVariant(
      variant: Entity.ProductVariation,
      formValue: {
        price?: number | null;
        stock?: number | null;
        image?: string | null;
      },
    ): Entity.ProductVariation {
      const index = store.variants().findIndex((v) => isEqual(v.attributes, variant.attributes));
      const variants = store.variants();
      const updateData: Partial<Entity.ProductVariation> = {};
      if (formValue.price !== null && formValue.price !== undefined) {
        updateData.price = formValue.price;
      }
      if (formValue.stock !== null && formValue.stock !== undefined) {
        updateData.stock = formValue.stock;
      }
      if (formValue.image !== null && formValue.image !== undefined) {
        updateData.image = formValue.image;
      }
      variants[index] = { ...variant, ...updateData };
      patchState(store, { variants });
      return variants[index];
    },
  })),
);

function generateVariants(attributes: Dtos.Attribute[]) {
  if (attributes.length === 0) {
    return [];
  }
  const valueArrays = attributes.map((attr) =>
    attr.values.map<Entity.VariationAttribute>((value) => ({
      id: new ObjectId().toHexString(),
      key: value.key,
      label: value.label,
      type: attr.type,
      typeLabel: attr.label,
      typeKey: attr.key,
    })),
  );

  return cartesianProduct(valueArrays).map((attributes) => {
    return {
      id: new ObjectId().toHexString(),
      attributes: attributes,
      price: 0,
      stock: 0,
    };
  });
}

function attributeValueFromVariationAttribute(attribute: Entity.VariationAttribute): Dtos.AttributeValue {
  return {
    key: attribute.key,
    label: attribute.label,
    fakeForTesting: false,
  };
}

function cartesianProduct(arrays: Entity.VariationAttribute[][]) {
  return arrays.reduce(
    (accumulator, currentArray) =>
      flatMap(accumulator, (combination) => map(currentArray, (item) => [...combination, item])),
    [[] as Entity.VariationAttribute[]],
  );
}

@Injectable({ providedIn: 'root' })
export class AttributesService {
  fetchAttributes() {
    return from(Promise.resolve(attributes));
  }
}
